import type { FC } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import type { Location } from 'react-router';
import { useLocation, useNavigate, useParams } from 'react-router';
import { Box, Button, Link, Tab, Tabs, Typography } from '@mui/material';
import type { FieldValues, UseFormReset } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { observer } from 'mobx-react-lite';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useEnqueueSnackbar, useModal, useScrollIntoTabView } from 'hooks';
import {
  RuleEditTitle,
  TabViewContainer,
  DiscardChangesDialog,
  RuleUpdatedMessage,
  RuleUIFormActionBlock,
} from '../../components';
import BasicValues from './BasicValues';
import { basicValuesSchema } from '../validation';
import { getGoogleRuleDetailsOptions, useUpdateGoogleRule, useCreateGoogleRule } from '../api';
import type { GoogleRuleResponseData } from '../api/types';
import { useCurrentTasksFormStore, useRuleNavigation } from '../../hooks';
import type { LevelValue, RuleType, RuleStatusValue, RuleTypeMutation, NotificationMessage } from '../../types';
import normalizeObject from '../../utils/normalizeObject';
import CircularSpinner from '../../../../components/common/CircularSpinner';
import RuleEditTasksForm from '../../components/RuleEditTasksForm';
import GoogleFilterGroupsContainer from '../../components/FilterGroupsContainer';
import googleFilterStore from '../stores/googleFiltersStore';
import googleTimetableStore from '../stores/googleTimetableStore';
import { useValidate } from '../../utils/useValidate';
import useRuleBlocker from '../../hooks/useRuleBlocker';
import useHydrateRuleData from '../../hooks/useHydrateRuleData';
import getGoogleInitialValues from '../utils/getGoogleInitialValues';
import { OpenInNew } from '@mui/icons-material';
import { tasksArraySchema } from '../../validation/tasks.schema';
import type { RuleTaskEntry } from '../../types';
import { notificationChannelOptions } from '../../api';
import TimeTableContainer from '../../components/TimeTableContainer';
import { omit } from 'lodash-es';
import RuleScriptDialog from '../../components/RuleScriptDialog';
import { useSuspenseQuery } from '@tanstack/react-query';

export interface BasicGoogleValues {
  name: string;
  project: number;
  ruleStatus: string;
  level: LevelValue;
  type: RuleType;
  description: string;
  notificationChannels: string[];
  notificationLevel: string;
  notificationMessage: NotificationMessage | null;
  adAccountIds: string[];
}

interface RuleEditProps {
  mode: RuleTypeMutation;
}

const EditGoogleRule: FC<RuleEditProps> = observer(({ mode }) => {
  const isModeRead = mode === 'read';
  const isModeDuplicate = mode === 'duplicate';
  const isModeEdit = mode === 'edit';

  const tasksFormStore = useCurrentTasksFormStore();
  const { ruleId } = useParams<{ ruleId: string }>();
  const { data: rule } = useSuspenseQuery(getGoogleRuleDetailsOptions(Number(ruleId)));
  const navigate = useNavigate();
  const { state: stateLocation } = useLocation() as Location<{ isDuplicated?: boolean }>;
  const { isOpen, open, close } = useModal();
  const { onShowAlert } = useEnqueueSnackbar();
  const [isChangesApplied, setIsChangesApplied] = useState(false);
  const { ruleListURL, ruleEditURL, ruleDuplicateURL } = useRuleNavigation();

  const { data: notificationChannels } = useSuspenseQuery(notificationChannelOptions);

  const getNotificationChannelsDTO = (ids: string[]) => {
    return notificationChannels.filter((nc) => ids.includes(String(nc.id)));
  };

  const scrollContainerRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const { tab, handleChangeTab } = useScrollIntoTabView(scrollContainerRef, contentRef);

  const getInitialValues = useMemo(() => getGoogleInitialValues(rule, mode), [rule, mode]);

  const formMethods = useForm<BasicGoogleValues>({
    defaultValues: getInitialValues,
    // @ts-expect-error schema correction
    resolver: yupResolver(basicValuesSchema),
    shouldFocusError: true,
    context: {
      duplicateMode: mode === 'duplicate',
      initialRuleName: rule.name,
    },
  });

  const { handleSubmit, reset, formState } = formMethods;

  const validate = useValidate({ isDirty: formState.isDirty, ruleLevel: undefined });
  const blocker = useRuleBlocker({ rule, isDirty: formState.isDirty, isRead: mode === 'read' });
  useHydrateRuleData({ rule, reset: reset as unknown as UseFormReset<FieldValues>, mode });

  const isBlocked = blocker.state === 'blocked';

  const { mutateAsync: updateRule, isPending: isUpdating } = useUpdateGoogleRule({
    config: {
      onSuccess: () => {
        onShowAlert('Changes saved successfully', { delayAlert: 3500 });
        setIsChangesApplied((v) => !v);
      },
    },
  });

  const { mutateAsync: duplicateRule, isPending: isDuplicating } = useCreateGoogleRule({
    config: {
      onSuccess: (data: GoogleRuleResponseData) => {
        navigate(ruleEditURL(data.id), { replace: true, state: { skipBlocker: true, isDuplicated: true } });
        onShowAlert('Rule duplicated', { delayAlert: 3500 });
      },
    },
  });

  const getSubmitPayload = async ({
    ruleStatus,
    description,
    project,
    level,
    name,
    adAccountIds,
    notificationMessage,
    notificationLevel,
    notificationChannels,
    type,
  }: BasicGoogleValues) => {
    const isValid = await validate(rule);
    if (!isValid) return;

    const tasks = tasksArraySchema.cast(tasksFormStore.tasksFormValues) as RuleTaskEntry[];

    return {
      id: Number(ruleId),
      project_id: project,
      name,
      status: ruleStatus as RuleStatusValue,
      description,
      data: {
        ...omit(rule.data, googleTimetableStore.scheduleInterval ? 'timetable' : 'schedule_interval'),
        type,
        filters: googleFilterStore.filterGroups,
        tasks: tasks,
        ...(googleTimetableStore.scheduleInterval
          ? { schedule_interval: googleTimetableStore.scheduleInterval }
          : { timetable: googleTimetableStore.timeTableDTO }),
        level: level as LevelValue,
        ad_account_ids: adAccountIds.map(String),
        notification_channels: getNotificationChannelsDTO(notificationChannels),
        notification_level: notificationLevel,
        ...(notificationMessage && {
          notification_message: {
            title: notificationMessage.title,
            body: notificationMessage.body,
          },
        }),
      },
    };
  };

  const onSubmit = async (data: BasicGoogleValues) => {
    const payload = await getSubmitPayload(data);
    if (payload) {
      try {
        if (isModeEdit) {
          await updateRule(payload);
        }

        if (isModeDuplicate) {
          await duplicateRule(payload);
        }
      } catch (error) {
        console.error(error);
      }
    }
  };

  const onDiscard = () => {
    if (isBlocked) {
      blocker.proceed();
    }
    reset();
    JSON.stringify(normalizeObject(rule.data), null, 2);
    close();
    navigate(ruleListURL);
  };

  const onClose = () => {
    if (isBlocked) {
      blocker.reset();
    }
    close();
  };

  const openLogs = () => {
    window.open(rule.logs_s3_folder_url, '_blank');
  };

  const handleNavigateToDuplicate = useCallback(() => {
    navigate(ruleDuplicateURL(rule.id), { replace: true });
  }, [navigate, ruleDuplicateURL, rule.id]);

  const isLoading = isUpdating || isDuplicating;

  if (isLoading) {
    return <CircularSpinner title={isDuplicating ? 'Duplicating rule' : 'Updating rule'} />;
  }

  return (
    <FormProvider {...formMethods}>
      <DiscardChangesDialog isOpen={isOpen || isBlocked} onClose={onClose} onDiscard={onDiscard} />
      <RuleScriptDialog isOpen={isOpen} onClose={close} rule={rule} />
      <RuleUIFormActionBlock
        mode={['update', 'create-to-some']}
        project={{ id: rule.project_id, name: rule.project_name }}
        isPending={isLoading}
        onDiscard={onDiscard}
        hasChanges={true}
        onSave={isModeRead ? handleNavigateToDuplicate : handleSubmit(onSubmit)}
        submitTitle={isModeDuplicate || isModeRead ? 'Duplicate' : 'Save changes'}
      />
      <Box sx={{ display: 'flex', flexGrow: 1, flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <RuleEditTitle name={rule.name} status={rule.status as RuleStatusValue} state={rule.state} mode={mode} />
          {isChangesApplied && <RuleUpdatedMessage text="Rule will be updated in 10 minutes or less." />}
          {stateLocation?.isDuplicated && (
            <RuleUpdatedMessage text="New rule has been created. It will be available to perform in 10 minutes or less." />
          )}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tab} onChange={handleChangeTab} variant="scrollable" scrollButtons="auto">
              <Tab value="basics" label="Basics" />
              <Tab value="filters" label="Filters" />
              <Tab value="tasks" label="Tasks" />
              <Tab value="timetable" label="Timetable" />
            </Tabs>

            <Box>
              <Button
                component={Link}
                href={'https://welltech.atlassian.net/wiki/spaces/MA1/pages/4971266090/User+Rule+builder+in+progress'}
                startIcon={<OpenInNew />}
                variant={'text'}
                color={'primary'}
                size={'medium'}
                target={'_blank'}
                sx={{ textTransform: 'none' }}
              >
                Setup guide
              </Button>
              <Button sx={{ textDecoration: 'underline', textTransform: 'inherit' }} onClick={openLogs}>
                <OpenInNewIcon sx={{ mr: 1 }} />
                <Typography variant="subtitle2">Logs in S3</Typography>
              </Button>
              <Button sx={{ textDecoration: 'underline', textTransform: 'inherit' }} onClick={open}>
                Rule script
              </Button>
            </Box>
          </Box>
        </Box>
        <Box
          ref={scrollContainerRef}
          sx={{ display: 'flex', flexGrow: 1, position: 'relative', overflow: 'auto', mr: -3 }}
        >
          <Box
            ref={contentRef}
            sx={{ display: 'flex', flexDirection: 'column', position: 'absolute', gap: 3, width: '100%', pr: 3 }}
          >
            <TabViewContainer id="basics">
              <BasicValues rule={rule} mode={mode} />
            </TabViewContainer>
            <TabViewContainer id="filters">
              <GoogleFilterGroupsContainer />
            </TabViewContainer>
            <TabViewContainer id="tasks">
              <RuleEditTasksForm />
            </TabViewContainer>
            <TabViewContainer id="timetable">
              <TimeTableContainer scheduledTimezone={rule.data.schedule_timezone} />
            </TabViewContainer>
          </Box>
        </Box>
      </Box>
    </FormProvider>
  );
});

export default EditGoogleRule;
